import React from 'react'
import { QuickFilters } from '../quick-filters'
import { elPt9, elRowGap4, elW3, FlexContainer, Icon, InputGroup } from '@reapit/elements'
import { useSearchParams } from 'react-router-dom'
import { css, cx } from '@linaria/core'
import { TTableRowAction } from '@reapitform/common-ui'
import { AgreementListItem } from 'apps/agent/src/core/api/types/agreements'
import {
  ActivityListModal,
  ChangeExpiryModal,
  DeleteDraftModal,
  ResendLinkModal,
  SendForSigningModal,
  SignAndCompleteModal,
  WithdrawModal,
} from '../../../../components/modals'
import { AgreementTable } from '../agreement-table'
import { useQueryClient } from '@tanstack/react-query'
import { AGREEMENT_QUERY_KEY } from '../../../../api/agreements/get-agreement-list'
import { getAgreementStatusSummaryQueryKey } from '../../../../api/agreements/agreement-status-summary'

export const AgreementOpenTab: React.FC = React.memo(() => {
  const queryClient = useQueryClient()

  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedAgreementId, setSelectedAgreementId] = React.useState<string[] | undefined>()
  const [selectedAgreement, setSelectedAgreement] = React.useState<AgreementListItem>()
  const [isOpenSendForSigningModal, setIsOpenSendForSigningModal] = React.useState<boolean>(false)
  const [isOpenChangeExpiryModal, setIsOpenChangeExpiryModal] = React.useState<boolean>(false)
  const [isOpenResendLinkModal, setIsOpenResendLinkModal] = React.useState<boolean>(false)
  const [isOpenWithdrawModal, setIsOpenWithdrawModal] = React.useState<boolean>(false)
  const [isOpenSignAndCompleteModal, setIsOpenSignAndCompleteModal] = React.useState<boolean>(false)
  const [isOpenDeleteDraftModal, setIsOpenDeleteDraftModal] = React.useState<boolean>(false)
  const [isOpenActivityModal, setIsOpenActivityModal] = React.useState<boolean>(false)

  const handleSearch = (event?: React.ChangeEvent<HTMLInputElement>) => {
    React.startTransition(() => {
      setSearchParams((prevParams) => {
        if (event?.target.value) prevParams.set('query', event?.target.value)
        else prevParams.delete('query')

        return prevParams
      })
    })
  }

  const rowActions: TTableRowAction<AgreementListItem>[] = React.useMemo(
    () => [
      {
        actionTitle: 'Change Expiry',
        onClick: (data) => {
          setSelectedAgreementId([data.referenceID])
          setIsOpenChangeExpiryModal(true)
        },
        onClickMultiple: (data) => {
          const agreementIds = data.map((currentRow) => currentRow.referenceID)
          setSelectedAgreementId(agreementIds)
          setIsOpenChangeExpiryModal(true)
        },
        visibleFor: (data: AgreementListItem) => ['Pending', 'Expired'].includes(data.status),
      },
      {
        actionTitle: 'Resend',
        onClick: (data) => {
          setSelectedAgreementId([data.referenceID])
          setIsOpenResendLinkModal(true)
        },
        onClickMultiple: (data) => {
          const agreementIds = data.map((currentRow) => currentRow.referenceID)
          setSelectedAgreementId(agreementIds)
          setIsOpenResendLinkModal(true)
        },
        visibleFor: (data: AgreementListItem) => ['Pending'].includes(data.status),
      },
      {
        actionTitle: 'Withdraw',
        onClick: (data) => {
          setSelectedAgreementId([data.referenceID])
          setIsOpenWithdrawModal(true)
        },
        onClickMultiple: (data) => {
          const agreementIds = data.map((currentRow) => currentRow.referenceID)
          setSelectedAgreementId(agreementIds)
          setIsOpenWithdrawModal(true)
        },
        visibleFor: (data: AgreementListItem) => ['Pending', 'Expired', 'Blocked', 'Signed'].includes(data.status),
      },
      {
        actionTitle: 'Sign and Complete',
        onClick: (data) => {
          setSelectedAgreementId([data.referenceID])
          setIsOpenSignAndCompleteModal(true)
        },
        onClickMultiple: (data) => {
          const agreementIds = data.map((currentRow) => currentRow.referenceID)
          setSelectedAgreementId(agreementIds)
          setIsOpenSignAndCompleteModal(true)
        },
        visibleFor: (data: AgreementListItem) => ['Signed'].includes(data.status),
      },
      {
        actionTitle: 'Delete',
        actionMultipleTitle: <Icon icon="trash" intent="low" iconSize="smallest" fontSize="inherit !important" />,
        onClick: (data) => {
          setSelectedAgreementId([data.referenceID])
          setIsOpenDeleteDraftModal(true)
        },
        onClickMultiple: (data) => {
          const agreementIds = data.map((currentRow) => currentRow.referenceID)
          setSelectedAgreementId(agreementIds)
          setIsOpenDeleteDraftModal(true)
        },
        visibleFor: (data: AgreementListItem) => ['Draft'].includes(data.status),
      },
      {
        actionTitle: 'View Activity',
        onClick: (data) => {
          setSelectedAgreement(data)
          setIsOpenActivityModal(true)
        },
        visibleFor: (data: AgreementListItem) => ['Pending', 'Expired', 'Blocked', 'Signed'].includes(data.status),
        disabledCombinedAction: true,
      },
    ],
    [],
  )

  const revalidateAgreementData = React.useCallback(() => {
    const agreementStatusKey = getAgreementStatusSummaryQueryKey()
    queryClient.invalidateQueries({ queryKey: [agreementStatusKey] })

    queryClient.refetchQueries({
      predicate: (query) => query.isActive() && query.queryKey.includes(AGREEMENT_QUERY_KEY[0]),
    })
  }, [queryClient])

  return (
    <FlexContainer tag="section" isFlexColumn className={cx(elRowGap4, elPt9)}>
      <QuickFilters />
      <InputGroup
        type="text"
        icon="search"
        onChange={handleSearch}
        className={cx(
          elW3,
          css`
            .el-input {
              height: 2rem !important;
            }
          `,
        )}
        placeholder="Search"
        defaultValue={searchParams.get('query') ?? ''}
      />
      <AgreementTable rowActions={rowActions} />
      <SendForSigningModal
        agreementId={selectedAgreementId}
        isOpen={isOpenSendForSigningModal}
        onClose={() => setIsOpenSendForSigningModal(false)}
        onFinish={revalidateAgreementData}
      />
      <ChangeExpiryModal
        agreementId={selectedAgreementId}
        isOpen={isOpenChangeExpiryModal}
        onClose={() => setIsOpenChangeExpiryModal(false)}
        onFinish={revalidateAgreementData}
      />
      <ResendLinkModal
        agreementId={selectedAgreementId}
        isOpen={isOpenResendLinkModal}
        onClose={() => setIsOpenResendLinkModal(false)}
        onFinish={revalidateAgreementData}
      />
      <WithdrawModal
        agreementId={selectedAgreementId}
        isOpen={isOpenWithdrawModal}
        onClose={() => setIsOpenWithdrawModal(false)}
        onFinish={revalidateAgreementData}
      />
      <SignAndCompleteModal
        agreementId={selectedAgreementId}
        isOpen={isOpenSignAndCompleteModal}
        onClose={() => setIsOpenSignAndCompleteModal(false)}
        onFinish={revalidateAgreementData}
      />
      <DeleteDraftModal
        agreementId={selectedAgreementId}
        isOpen={isOpenDeleteDraftModal}
        onClose={() => setIsOpenDeleteDraftModal(false)}
        onFinish={revalidateAgreementData}
      />
      {selectedAgreement && (
        <ActivityListModal
          isOpen={isOpenActivityModal}
          documentId={selectedAgreement.referenceID}
          isOpenChange={(isOpen) => setIsOpenActivityModal(isOpen)}
          hideViewAgreementButton
          agreementName={selectedAgreement.formName}
        />
      )}
    </FlexContainer>
  )
})
